import BenchlingConfig from 'BenchlingConfig';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface ProgressiveDisclosureTenantContext {
  isAcademicTenant: boolean;
}

export type ProgressiveDisclosurePrompt =
  | string
  | ((context: ProgressiveDisclosureTenantContext) => string | null);

/** Raw option definition; resolved at read time via {@link getProgressiveDisclosureCategories}. */
export interface ProgressiveDisclosureOptionDefinition {
  id: string;
  label: string | ((context: ProgressiveDisclosureTenantContext) => string);
  prompt: ProgressiveDisclosurePrompt;
}

/** Tenant-resolved option surfaced to UI and onboarding flows. */
export interface ProgressiveDisclosureOption {
  id: string;
  label: string;
  prompt: string;
}

export interface ProgressiveDisclosureCategoryDefinition {
  id: string;
  label: string;
  icon: BenchlingIconName;
  options: Array<ProgressiveDisclosureOptionDefinition>;
}

export interface ProgressiveDisclosureCategory {
  id: string;
  label: string;
  icon: BenchlingIconName;
  options: Array<ProgressiveDisclosureOption>;
}

export const PROGRESSIVE_DISCLOSURE_CATEGORIES: Array<ProgressiveDisclosureCategoryDefinition> = [
  {
    id: 'capture-data',
    label: 'Capture data',
    icon: 'file-chart-column-outline',
    options: [
      {
        id: 'transcribe-handwritten-notes',
        label: 'Transcribe my handwritten notes',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? 'I want to transcribe my handwritten notes. Help me import them into a new notebook entry draft.'
            : null,
      },
      {
        id: 'register-entities',
        label: 'Register entities from file',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? null
            : 'I want to import entities into Benchling. Please ask me one or two questions to determine what entities need to be registered and any context on the data source to assist with registration. Then generate a draft entry with the appropriate registration tables for data capture.',
      },
      {
        id: 'import-results',
        label: ({isAcademicTenant}) => `Import ${isAcademicTenant ? 'data' : 'results'} from file`,
        prompt: ({isAcademicTenant}) => {
          if (isAcademicTenant) {
            return 'I want to import data into Benchling. Please ask me one or two questions to determine what data needs to be imported and any context on the data source to assist with import. Then generate a draft entry.';
          }
          return 'I want to import results into Benchling. Please ask me one or two questions to determine what results need to be created and any context on the data source to assist with import. Then generate a draft entry with the appropriate result tables for data capture.';
        },
      },
      {
        id: 'create-notebook-entry',
        label: 'Create and fill a notebook entry',
        prompt: ({isAcademicTenant}) =>
          `I want to draft a notebook entry using AI. Start a draft prefilled with placeholder sections for a standard notebook entry and ask me one or two questions to determine what the purpose of the entry is, ${isAcademicTenant ? 'if it should follow a specific structure.' : 'if it should follow a specific template, and any other specific needs for its structure.'}`,
      },
    ],
  },
  {
    id: 'analyze-data',
    label: 'Analyze data',
    icon: 'magnifying-glass-chart-outline',
    options: [
      {
        id: 'statistical-analysis',
        label: 'Run statistical analysis on data',
        prompt: ({isAcademicTenant}) => {
          const recentData = isAcademicTenant ? 'data' : 'result data';
          return `I want to run statistical analyses on my recent ${recentData}. Please ask me one or two questions to confirm the type of statistical analysis and which data it should be run on.`;
        },
      },
      {
        id: 'chart-results',
        label: 'Chart and plot results',
        prompt: ({isAcademicTenant}) => {
          const recentData = isAcademicTenant ? 'data' : 'result data';
          return `I want to plot and chart my recent ${recentData}. Please ask me one or two questions to confirm what type of charts or plots are needed and which ${recentData} it should be run on.`;
        },
      },
      {
        id: 'qc-check',
        label: 'QC check my data',
        prompt:
          "I want to do QC checks on my recently captured data. Please ask me one or two questions to determine which data I'm interested in QC checking and any specific QC requirements I'm trying to compare against.",
      },
    ],
  },
  {
    id: 'experiment-design',
    label: 'Design experiments',
    icon: 'flask',
    options: [
      {
        id: 'design-screening-experiment',
        label: 'Design a screening experiment',
        prompt:
          'I want to design a structured experiment using a DoE approach. Please ask me one or two questions to determine what factors and responses I want to test, then draft an experimental design.',
      },
      {
        id: 'optimize-assay-process',
        label: 'Optimize an assay or process',
        prompt: ({isAcademicTenant}) =>
          `I want to optimize an assay or process by varying several factors${isAcademicTenant ? '' : ' at once'}. Please ask me one or two questions about my factors, their ranges, and how many runs I can fit, then draft an experimental design that finds the best conditions efficiently.`,
      },
      {
        id: 'design-custom-experiment',
        label: 'Design a custom experiment',
        prompt:
          'I want to design an experiment. Please ask me one or two questions to understand my hypothesis and key variables, then draft a structured experimental plan.',
      },
    ],
  },
  {
    id: 'generate-hypotheses',
    label: 'Generate hypotheses',
    icon: 'lightbulb',
    options: [
      {
        id: 'investigate-unexpected-result',
        label: 'Investigate an unexpected result',
        prompt:
          "My results contradict what's reported in the literature. Please ask me one or two questions about my data and the expected mechanism, then reason across my notebook data and the published literature to generate a few plausible hypotheses, ranked by likelihood.",
      },
      {
        id: 'generate-mechanistic-hypotheses',
        label: 'Generate mechanistic hypotheses',
        prompt:
          "I want to generate hypotheses for why my target or pathway is behaving a certain way. Please ask me one or two questions about my system and observations, then draw on my program's data alongside the literature to draft a ranked list of plausible mechanisms with supporting rationale.",
      },
      {
        id: 'stress-test-hypothesis',
        label: 'Stress-test my hypothesis',
        prompt:
          'I have an initial hypothesis I want to pressure-test. Please ask me one or two questions about it, then check it against both my own prior experiments and the published literature to suggest alternative or refined hypotheses I may not have considered.',
      },
    ],
  },
  {
    id: 'write-reports',
    label: 'Write reports',
    icon: 'pen-to-square-outline',
    options: [
      {
        id: 'experimental-report',
        label: 'Write a lab report',
        prompt:
          'I want to write a lab report on my recent work. Please ask me one or two questions to determine what the report should be on, and any requirements there are for format, content or style.',
      },
      {
        id: 'regulatory-report',
        label: 'Write a regulatory report',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? null
            : "I want to write a regulatory report on the programs or projects I've been involved in. Please ask me one or two questions to determine the specific type of regulatory report needed, and confirm the scope and topic.",
      },
      {
        id: 'status-report',
        label: 'Write a status report',
        prompt:
          'I want to write a status report based on my recent work. Offer a suggestion for what this report could contain and ask me one or two questions to confirm the scope of the report and its intended audience.',
      },
      {
        id: 'literature-review-for-paper',
        label: 'Conduct a literature review for my paper',
        prompt: ({isAcademicTenant}) =>
          isAcademicTenant
            ? "Draft the introduction and background for my paper using my experimental data and relevant literature from the web. Ask me one or two questions to nail down the topic and scope of what's needed, and include citations in the generated draft."
            : null,
      },
    ],
  },
];

function resolvePrompt(
  prompt: ProgressiveDisclosurePrompt,
  context: ProgressiveDisclosureTenantContext
): string | null {
  return typeof prompt === 'function' ? prompt(context) : prompt;
}

function resolveLabel(
  label: ProgressiveDisclosureOptionDefinition['label'],
  context: ProgressiveDisclosureTenantContext
): string {
  return typeof label === 'function' ? label(context) : label;
}

function resolveOption(
  option: ProgressiveDisclosureOptionDefinition,
  context: ProgressiveDisclosureTenantContext
): ProgressiveDisclosureOption | null {
  const prompt = resolvePrompt(option.prompt, context);
  if (prompt == null) {
    return null;
  }
  return {id: option.id, label: resolveLabel(option.label, context), prompt};
}

/** Progressive-disclosure categories and options visible in the current tenant context. */
export function getProgressiveDisclosureCategories(): Array<ProgressiveDisclosureCategory> {
  const context: ProgressiveDisclosureTenantContext = {isAcademicTenant: !BenchlingConfig.PRIVATE_DEPLOY};
  return PROGRESSIVE_DISCLOSURE_CATEGORIES.map((category) => ({
    id: category.id,
    label: category.label,
    icon: category.icon,
    options: category.options
      .map((option) => resolveOption(option, context))
      .filter((option): option is ProgressiveDisclosureOption => option != null),
  }));
}
